import { useEffect, useRef, useState } from "react";

export default function HistoriaSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="historia"
      ref={ref}
      className="py-28 md:py-40 relative overflow-hidden"
      style={{ background: "linear-gradient(160deg, #001A3D 0%, #002D62 50%, #001A3D 100%)" }}
    >
      {/* Background image overlay */}
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://public.readdy.ai/ai/img_res/c87664686d590d1b4fafdeef49f3d96a.jpg"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Gold accent lines */}
      <div className="absolute top-0 left-0 w-full h-px" style={{ background: "linear-gradient(90deg, transparent, #D4AF37, transparent)" }} />
      <div className="absolute bottom-0 left-0 w-full h-px" style={{ background: "linear-gradient(90deg, transparent, #D4AF37, transparent)" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-16">

        {/* Header */}
        <div
          className={`text-center mb-20 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <p className="text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: "#D4AF37", letterSpacing: "4px" }}>
            El Conflicto
          </p>
          <h2
            className="font-bold mb-6"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2rem, 4vw, 3.2rem)",
              color: "#FFFFFF",
              lineHeight: 1.2
            }}
          >
            La Jaula de las Preguntas
          </h2>
          <p
            className="max-w-2xl mx-auto"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "1.1rem",
              color: "rgba(255,255,255,0.7)",
              lineHeight: 1.7
            }}
          >
            Cuando la vida de un hombre construida sobre el Derecho, la razón y el esfuerzo se detiene abruptamente, solo quedan dos preguntas: ¿Por qué? ¿Y ahora qué?
          </p>
        </div>

        {/* Dual Panel */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-0 mb-20 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ transitionDelay: "0.3s" }}
        >
          {/* Before Panel */}
          <div
            className="p-10 md:p-14 rounded-t-2xl lg:rounded-l-2xl lg:rounded-tr-none"
            style={{
              background: "rgba(255,255,255,0.04)",
              borderRight: "1px solid rgba(212,175,55,0.3)",
              borderBottom: "1px solid rgba(212,175,55,0.3)"
            }}
          >
            <div className="w-14 h-14 flex items-center justify-center rounded-full mb-6" style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.4)" }}>
              <i className="ri-briefcase-4-line text-2xl" style={{ color: "#D4AF37" }} />
            </div>
            <h3
              className="font-bold mb-4"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "1.8rem",
                color: "#FFFFFF"
              }}
            >
              El Empresario
            </h3>
            <p
              className="leading-relaxed"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "1rem",
                color: "rgba(255,255,255,0.72)",
                lineHeight: 1.85
              }}
            >
              Carlos era un hombre de orden. Sus días transcurrían entre contratos, ganado, aulas universitarias y el calor de su hogar en el Tachira. Había construido con sus manos —y con la ley— una vida digna. Tenía nombre, tenía historia, tenía propósito. Era el tipo de hombre que cree que si haces las cosas bien, el mundo responde en consecuencia.
            </p>
            <div className="mt-8 pt-6" style={{ borderTop: "1px solid rgba(212,175,55,0.2)" }}>
              <p className="text-sm italic" style={{ color: "rgba(212,175,55,0.8)" }}>
                &ldquo;Una vida construida sobre la ley, la tierra y la familia.&rdquo;
              </p>
            </div>
          </div>

          {/* During Panel */}
          <div
            className="p-10 md:p-14 rounded-b-2xl lg:rounded-r-2xl lg:rounded-bl-none"
            style={{
              background: "rgba(212,175,55,0.05)",
              borderLeft: "1px solid rgba(212,175,55,0.3)",
              borderTop: "1px solid rgba(212,175,55,0.3)"
            }}
          >
            <div className="w-14 h-14 flex items-center justify-center rounded-full mb-6" style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.4)" }}>
              <i className="ri-lock-2-line text-2xl" style={{ color: "#D4AF37" }} />
            </div>
            <h3
              className="font-bold mb-4"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "1.8rem",
                color: "#FFFFFF"
              }}
            >
              El Cautiverio
            </h3>
            <p
              className="leading-relaxed"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "1rem",
                color: "rgba(255,255,255,0.72)",
                lineHeight: 1.85
              }}
            >
              67 días. Un cambuche de barro. Sin luz natural, sin certezas, sin el lenguaje del Derecho que siempre le había dado respuestas. Solo preguntas que ningún libro de leyes podía responder: ¿Fue un error? ¿Fue un plan? ¿Sobreviviré? En ese silencio forzado, su formación académica se encontró cara a cara con algo que ninguna universidad enseña: la fe más pura.
            </p>
            <div className="mt-8 pt-6" style={{ borderTop: "1px solid rgba(212,175,55,0.2)" }}>
              <p className="text-sm italic" style={{ color: "rgba(212,175,55,0.8)" }}>
                &ldquo;Donde la lógica termina, comienza la fe.&rdquo;
              </p>
            </div>
          </div>
        </div>

        {/* Central Faith Element */}
        <div
          className={`flex flex-col items-center transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ transitionDelay: "0.6s" }}
        >
          <div
            className="relative rounded-2xl overflow-hidden mb-6"
            style={{
              width: "200px",
              height: "200px",
              border: "3px solid #D4AF37",
              boxShadow: "0 0 60px rgba(212,175,55,0.3)"
            }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=Our%20Lady%20of%20Perpetual%20Help%20religious%20icon%20painting%2C%20Byzantine%20style%20gold%20background%2C%20Virgin%20Mary%20holding%20baby%20Jesus%2C%20traditional%20Catholic%20devotional%20art%2C%20warm%20golden%20tones%2C%20sacred%20religious%20imagery%2C%20detailed%20iconography&width=200&height=200&seq=faith001&orientation=squarish"
              alt="Nuestra Señora del Perpetuo Socorro"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,45,98,0.4), transparent)" }} />
          </div>
          <p
            className="text-center italic"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "1rem",
              color: "#D4AF37"
            }}
          >
            La luz en el cambuche de barro
          </p>
          <p
            className="text-center mt-2 max-w-md"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "0.9rem",
              color: "rgba(255,255,255,0.5)"
            }}
          >
            Nuestra Señora del Perpetuo Socorro — la presencia que nunca abandonó a Carlos en sus 67 días de cautiverio.
          </p>
        </div>
      </div>
    </section>
  );
}
